/**
 * CORE-1693 — одна точка входа для «Репост в свою ленту».
 *
 * Карточка поста, события, практики или записи вызывает
 * `openRepost(target, preview)`; провайдер рисует окно репоста поверх
 * навигатора (как ReportContext для жалоб) и после публикации показывает
 * короткое «Опубликовано в вашей ленте». Гость уводится во вход через
 * AuthCTA, после входа окно открывается само.
 */
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useAuth } from './AuthContext';
import { useAuthCTA } from './AuthCTAContext';
import { useI18n } from '../i18n';
import { RepostComposerModal } from '../components/repost/RepostComposerModal';
import type { RepostPreview } from '../services/repost';
import type { MasterPost, RepostTarget } from '../types/auth';

export interface RepostRequest {
  target: RepostTarget;
  preview: RepostPreview;
}

interface RepostContextType {
  openRepost: (target: RepostTarget, preview: RepostPreview, onPublished?: (post: MasterPost) => void) => void;
}

const RepostContext = createContext<RepostContextType>({
  openRepost: () => {},
});

export const useRepost = () => useContext(RepostContext);

export const RepostProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, hasRole } = useAuth();
  const { showAuthCTA } = useAuthCTA();
  const { t } = useI18n('common');
  const [request, setRequest] = useState<(RepostRequest & { onPublished?: (post: MasterPost) => void }) | null>(null);

  const openRepost = useCallback<RepostContextType['openRepost']>((target, preview, onPublished) => {
    const next = { target, preview, onPublished };
    if (!isAuthenticated) {
      showAuthCTA('gated_action', t('repost.signIn'), () => setRequest(next));
      return;
    }
    setRequest(next);
  }, [isAuthenticated, showAuthCTA, t]);

  return (
    <RepostContext.Provider value={{ openRepost }}>
      {children}
      <RepostComposerModal
        request={request}
        asMaster={hasRole('Master')}
        onClose={() => setRequest(null)}
        onPublished={(post) => {
          request?.onPublished?.(post);
          setRequest(null);
        }}
      />
    </RepostContext.Provider>
  );
};
